import React, { useState, useEffect } from 'react';

import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import { staticRoutesWithCustomSidebar } from 'Deer/constants/staticRoutesWithCustomSidebar';
import APIError from 'components/AlertScreens/APIError';
import EmptyError from 'components/AlertScreens/EmptyError';
import ListSkeletonLoader from 'components/AlertScreens/ListSkeletonLoader';
import FooterActions from 'components/Review/FooterActions/FooterActions';
import HeaderActions from 'components/Review/HeaderActions/HeaderActions';
import MeshTab from 'components/Review/MeshTab/MeshTab';
import useAsync from 'components/hooks/useAsync';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import HelloBar from 'screens/performanceReview/LandingPage/HelloBar';
import styled from 'styled-components';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { REVIEW_FORM_COMPLETION_STATUS, REVIEW_QUESTION_TYPES } from 'constants/ProjectConstants';
import { goToReviewTemplateListRoute, insertDisplayOrderInArray } from 'constants/helper';
import { PreviewIcon } from 'constants/icons';
import { routes } from 'constants/routes';

import { showErrorSnackbar } from 'services/snackbar';
import { getTemplateStructure, saveTemplateQuestions } from 'services/templates/review';

import { Typography10 } from 'uiToolkit/Typography';

import { useReviewTemplate } from '../common/ReviewTemplateContext';
import CompetencyReview from './CompetencyReview';
import GoalReview from './GoalReview';
import OverAllQuestions from './OverAllQuestions';

const MAX_FIXED_WEIGHT = Number(100).toFixed(2);
const MIN_FIXED_WEIGHT = Number(0).toFixed(2);

const disableType = {
  TAB: 'TAB',
  NEXT: 'NEXT',
  PREV: 'PREV',
};

const defaultTabs = [
  {
    id: REVIEW_QUESTION_TYPES.PERFORMANCE,
    title: 'Goal Review',
    subtitle: '',
  },
  {
    id: REVIEW_QUESTION_TYPES.POTENTIAL,
    title: 'Competency Review',
    subtitle: '',
  },
  {
    id: REVIEW_QUESTION_TYPES.QNA,
    title: 'Overall Questions',
    subtitle: '',
  },
];

const ReviewTemplateForm = () => {
  const [activeTab, setActiveTab] = useState(1);
  const [tabs, setTabs] = useState(defaultTabs);
  const [saveLoading, setSaveLoading] = useState(null);
  const [showHelloBar, setShowHelloBar] = useState(true);

  const history = useHistory();
  const dispatch = useDispatch();
  const {
    state: { META, PERFORMANCE, POTENTIAL, QNA },
    setMeta,
  } = useReviewTemplate();

  const { data: metaData, isLoading, isError, run } = useAsync();

  const { templateId } = useParams();

  const getTemplateMeta = () => {
    run(getTemplateStructure({ templateId }));
  };

  useEffect(() => {
    if (templateId) {
      getTemplateMeta();
    }
  }, []);

  useEffect(() => {
    if (metaData) {
      const allowedTabs = tabs.filter(
        (tab) =>
          metaData.templateSetting.overallRatingDistribution[tab.id] !== null &&
          metaData.templateSetting.overallRatingDistribution[tab.id] >= 0
      );
      setTabs(allowedTabs);
      setActiveTab(allowedTabs[0].id);
      setMeta({ meta: metaData });
    }
  }, [metaData]);

  const handleTabChange = (newTab) => {
    saveForm()
      .then((res) => {
        setActiveTab(newTab);
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message || 'Something went wrong!')))
      .finally(() => setSaveLoading(false));
  };

  const handlePreviousClick = () => {
    const activeTabIndex = tabs.findIndex((tab) => tab.id === activeTab);
    saveForm()
      .then((res) => {
        if (activeTabIndex === 0) {
          goToReviewTemplateListRoute(history);
        } else {
          setActiveTab(tabs[activeTabIndex - 1].id);
        }
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message || 'Something went wrong!')))
      .finally(() => setSaveLoading(false));
  };

  const handleNextClick = () => {
    const activeTabIndex = tabs.findIndex((tab) => tab.id === activeTab);
    saveForm()
      .then((res) => {
        if (activeTabIndex === tabs.length - 1) {
          goToReviewTemplateListRoute(history);
        } else {
          setActiveTab(tabs[activeTabIndex + 1].id);
        }
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message || 'Something went wrong!')))
      .finally(() => setSaveLoading(false));
  };

  const saveForm = () => {
    setSaveLoading(true);
    const body = {
      competencyQuestions: null,
      qnaQuestions: null,
      goalQuestions: null,
    };

    if (activeTab === REVIEW_QUESTION_TYPES.PERFORMANCE) {
      body.goalQuestions = insertDisplayOrderInArray(PERFORMANCE.templateQuestions);
    }
    if (activeTab === REVIEW_QUESTION_TYPES.POTENTIAL) {
      const competencyQuestions = {};
      Object.keys(POTENTIAL.templateQuestions).forEach(
        (key) => (competencyQuestions[key] = insertDisplayOrderInArray(POTENTIAL.templateQuestions[key]))
      );
      body.competencyQuestions = competencyQuestions;
    }
    if (activeTab === REVIEW_QUESTION_TYPES.QNA) {
      body.qnaQuestions = insertDisplayOrderInArray(QNA.templateQuestions);
    }
    return saveTemplateQuestions({ templateId, section: activeTab, body }).then((res) => res);
  };

  const handlePreviewClick = () => {
    saveForm()
      .then((res) => {
        const path = checkNewNavEnabled()
          ? staticRoutesWithCustomSidebar.reviewCycleTemplatePreview.replace(':templateId', META.id)
          : routes.template.review.preview.replace(':templateId', META.id);
        window.open(path, '_blank');
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message || 'Something went wrong!')))
      .finally(() => setSaveLoading(false));
  };

  const getIsActionDisabled = ({ type = disableType.NEXT }) => {
    if (activeTab === REVIEW_QUESTION_TYPES.PERFORMANCE) {
      if (type === disableType.NEXT) {
        return !PERFORMANCE.templateQuestions?.length;
      }
      return false;
    }
    if (activeTab === REVIEW_QUESTION_TYPES.POTENTIAL) {
      const questionsMap = POTENTIAL.templateQuestions;
      const questionPerCompetency = Object.keys(questionsMap).map((key) => questionsMap[key].length);
      const totalWeightage = Object.keys(questionsMap).reduce(
        (acc, curr) =>
          acc +
          questionsMap[curr].reduce((accu, current) => {
            if (!Number.isNaN(current.weightage)) {
              if (current.question.analyticsQuestionType === 'RATING') {
                return accu + Number(current.weightage);
              }
              return accu;
            }
          }, 0),
        0
      );
      const fixedTotalWeight = Number(totalWeightage)?.toFixed(2);
      if (type === disableType.NEXT) {
        return (
          !questionPerCompetency.filter((comp) => comp > 0).length ||
          (fixedTotalWeight !== MIN_FIXED_WEIGHT && fixedTotalWeight !== MAX_FIXED_WEIGHT)
        );
      }
      if (questionPerCompetency.filter((comp) => comp > 0).length) {
        const res = fixedTotalWeight !== MIN_FIXED_WEIGHT && fixedTotalWeight !== MAX_FIXED_WEIGHT;
        return res;
      }
      return false;
    }
    if (activeTab === REVIEW_QUESTION_TYPES.QNA) {
      const totalSumWeightage = QNA.templateQuestions?.reduce((acc, curr) => {
        if (!Number.isNaN(curr.weightage)) {
          if (curr.question.analyticsQuestionType === 'RATING') {
            return acc + Number(curr.weightage);
          }
          return acc;
        }
      }, 0);
      const fixedTotalWeight = Number(totalSumWeightage)?.toFixed(2);
      if (type === disableType.NEXT) {
        return (
          !QNA.templateQuestions?.length ||
          (fixedTotalWeight !== MIN_FIXED_WEIGHT && fixedTotalWeight !== MAX_FIXED_WEIGHT)
        );
      }
      if (QNA.templateQuestions?.length) {
        return fixedTotalWeight !== MIN_FIXED_WEIGHT && fixedTotalWeight !== MAX_FIXED_WEIGHT;
      }
      return false;

      return;
    }
    return true;
  };

  const headerActions = [
    {
      text: 'Preview',
      color: 'white',
      borderColor: getIsActionDisabled({}) || saveLoading ? '' : 'primary',
      fontColor: 'primary',
      onClick: handlePreviewClick,
      disabled: getIsActionDisabled({}) || saveLoading,
      icon: <PreviewIcon size="fixed12px" color={getIsActionDisabled({}) || saveLoading ? '' : 'primary'} />,
    },
  ];

  const footerActions = [
    {
      text: 'Back',
      color: 'white',
      borderColor: 'primary',
      fontColor: 'primary',
      onClick: handlePreviousClick,
      disabled: getIsActionDisabled({ type: disableType.PREV }) || saveLoading,
    },
    {
      text: tabs.findIndex((item) => item.id === activeTab) === tabs.length - 1 ? 'Submit' : 'Next',
      color: 'blue',
      borderColor: 'primary',
      fontColor: 'white',
      onClick: handleNextClick,
      disabled: getIsActionDisabled({ type: disableType.NEXT }) || saveLoading,
    },
  ];

  const icons = {
    [REVIEW_FORM_COMPLETION_STATUS.IN_PROGRESS]: <WarningRoundedIcon />,
    [REVIEW_FORM_COMPLETION_STATUS.COMPLETED]: <CheckRoundedIcon />,
  };

  return (
    <>
      {isError ? (
        <APIError />
      ) : isLoading ? (
        <ListSkeletonLoader />
      ) : META.id ? (
        <FlexContainer>
          <HeaderActions
            actions={headerActions}
            primaryText={
              <Typography10 display="inline" variant="h6" color="textPrimary">
                {META.name}
              </Typography10>
            }
          />
          {META.ongoingReviewCycleCount && META.ongoingReviewCycleCount > 0 && showHelloBar ? (
            <HelloBar
              message={`This template is being used across ${META.ongoingReviewCycleCount} ongoing review cycles.\n\nNote: Editing the questions in this template will update the experience for users who are in progress or haven't started filling out the review forms. Although, it won't affect the users who have already submitted their review forms.\n\nTo ensure all users for the review cycle see the updated questions, the reviews for these users need to be re-opened by contacting the mesh team`}
              handleClose={() => setShowHelloBar(false)}
            />
          ) : null}
          <BodyContainer>
            <MeshTab
              tabs={tabs}
              icons={icons}
              active={activeTab}
              onChange={handleTabChange}
              disabled={getIsActionDisabled({ type: disableType.TAB })}
            />

            {activeTab === REVIEW_QUESTION_TYPES.PERFORMANCE && <GoalReview />}
            {activeTab === REVIEW_QUESTION_TYPES.POTENTIAL && <CompetencyReview />}
            {activeTab === REVIEW_QUESTION_TYPES.QNA && <OverAllQuestions />}
          </BodyContainer>
          <FooterActions actions={footerActions} />
        </FlexContainer>
      ) : (
        <EmptyError />
      )}
    </>
  );
};

export default ReviewTemplateForm;

const BodyContainer = styled.div`
  flex-grow: 1;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    width: 0;
    background: transparent;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
